import React, { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Assets, Colors, FontFamily, Input, Main, Models, PrimaryButton } from "utils/imports.utils";
import { ButtonSize, ButtonTypes } from "utils/interface.utils";
import "./reset_password.screen.scss";
import { useSetState } from "utils/functions.utils";

const ERROR_VISIBLE_MS = 5000;

const ResetPassword = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const resetState = location.state as { email?: string; reset_password_hash?: string } | null;
    const reset_password_hash = resetState?.reset_password_hash;

    const [state,setState] = useSetState({
        password: "",
        confirmPassword: "",
        error: "",
        errorField: "",
        activity: false,
    })

    useEffect(() => {
        if (!reset_password_hash) {
            navigate('/forgot-password');
        }
    }, [reset_password_hash, navigate]);

    // The message and the red outline clear themselves after a few seconds.
    useEffect(() => {
        if (!state.error) return;
        const timer = setTimeout(() => {
            setState({ error: "", errorField: "" });
        }, ERROR_VISIBLE_MS);
        return () => clearTimeout(timer);
    }, [state.error]);

    const handleReset = async () => {
        if (state.password !== state.confirmPassword) {
            setState({ error: "Passwords do not match", errorField: "confirm" });
            return;
        }
        setState({ error: "", errorField: "", activity: true });
        try {
            await Models.auth.resetPassword({ reset_password_hash, password: state.password });
            navigate('/reset-password-successful');
        } catch (error: any) {
            const message = typeof error === "string" ? error : "Failed to reset password. Please try again.";
            const validationPrefix = "Error validating request body.";
            setState({
                error: message.startsWith(validationPrefix) ? message.slice(validationPrefix.length).trim() : message,
                errorField: "password",
            });
        } finally {
            setState({ activity: false });
        }
    };

    return (
        <Main backgroundColor={Colors.screenBackground} isRenderOnlyLogo={true} logoSize={{width: "200px", height: "83px"}}>
            <div className="reset_password">
                <div className="reset_password_container">
                    <div className="reset_password_header">
                        <div className="reset_password_header_title">Reset Password</div>
                    </div>
                    <div className="reset_password_form">
                        <div className={`reset_password_form_input ${state.errorField === "password" ? "reset_password_form_input_error" : ""}`}>
                            <Input
                                label="New Password"
                                placeholder="Enter New Password"
                                type="password"
                                value={state.password}
                                onChange={(e) => setState({ password: e.target.value, error: "", errorField: "" })}
                            />
                        </div>
                        <div className={`reset_password_form_input ${state.errorField === "confirm" ? "reset_password_form_input_error" : ""}`}>
                            <Input
                                label="Password"
                                placeholder="Enter Password"
                                type="password"
                                icon={Assets.eye_slash}
                                iconPosition="end"
                                value={state.confirmPassword}
                                onChange={(e) => setState({ confirmPassword: e.target.value, error: "", errorField: "" })}
                            />
                        </div>
                        {state.error && (
                            <div className="input_field_error reset_password_form_error" role="alert">
                                <span className="reset_password_form_error_icon" aria-hidden="true">!</span>
                                <span>{state.error}</span>
                            </div>
                        )}
                    </div>
                    <div className="reset_password_buttons">
                        <div className="reset_password_buttons_reset">
                            <PrimaryButton
                                onClick={handleReset}
                                type={ButtonTypes.PRIMARY_GRADIENT}
                                text="Reset"
                                size={ButtonSize.LARGE}
                                fontFamily={FontFamily.medium}
                                lineHeight="28px"
                                activity={state.activity}
                                disabled={state.activity || !state.password || !state.confirmPassword}
                            />
                        </div>
                        <div className="reset_password_buttons_back">
                            <PrimaryButton
                                onClick={() => navigate('/enter-otp')}
                                text="Back"
                                type={ButtonTypes.TEXT_UNDERLINE}
                                size={ButtonSize.SMALL}
                                fontFamily={FontFamily.semiBold}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </Main>
    )
}

export default ResetPassword;
